export const TILE_UPLOAD_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
export const TILE_UPLOAD_MAX_CANVAS_PIXELS = 16_000_000;

export interface TileUploadPayload {
  base64: string;
  mimeType: 'image/webp';
}

interface TileUploadDeps {
  transcodeToWebp?: (file: File) => Promise<Blob>;
  readBlobAsDataUrl?: (blob: Blob) => Promise<string>;
}

export function isSupportedTileUploadFile(file: Pick<File, 'type'>): boolean {
  return TILE_UPLOAD_MIME_TYPES.has(file.type);
}

export async function prepareTileImageUpload(
  file: File,
  deps: TileUploadDeps = {},
): Promise<TileUploadPayload> {
  if (!isSupportedTileUploadFile(file)) {
    throw new Error('That file is not supported - pick a PNG, JPG, or WebP.');
  }

  const source =
    file.type === 'image/webp' ? file : await (deps.transcodeToWebp ?? transcodeFileToWebp)(file);

  if (source.type !== 'image/webp') {
    throw new Error('The browser could not convert that image to WebP.');
  }

  const dataUrl = await (deps.readBlobAsDataUrl ?? readBlobAsDataUrl)(source);
  return {
    base64: extractBase64Payload(dataUrl, 'image/webp'),
    mimeType: 'image/webp',
  };
}

function extractBase64Payload(dataUrl: string, expectedMimeType: string): string {
  const prefix = `data:${expectedMimeType};base64,`;
  if (!dataUrl.startsWith(prefix)) {
    throw new Error('The browser returned an unexpected tile image payload.');
  }
  const base64 = dataUrl.slice(prefix.length);
  if (base64.length === 0) {
    throw new Error('The browser returned an empty tile image payload.');
  }
  return base64;
}

async function readBlobAsDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Converts a browser-decodable image file to WebP using a bounded canvas.
 *
 * Args:
 *   file: Source image file to decode and transcode.
 *
 * Returns:
 *   A WebP blob encoded by the browser canvas implementation.
 *
 * Raises:
 *   Error: When the image cannot be decoded, is too large, has invalid
 *     dimensions, or the browser canvas cannot encode WebP.
 */
async function transcodeFileToWebp(file: File): Promise<Blob> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      throw new Error('The image has no drawable dimensions.');
    }
    const pixelCount = width * height;
    if (pixelCount > TILE_UPLOAD_MAX_CANVAS_PIXELS) {
      // Canvas draw allocates an RGBA buffer on the renderer main thread.
      throw new Error(
        `That image is too large to upload (${width}x${height}). ` +
          'Pick an image that is 16 megapixels or smaller.',
      );
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('The browser could not prepare the tile image canvas.');
    }
    ctx.drawImage(image, 0, 0, width, height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (!result) {
            reject(new Error('The browser could not convert that image to WebP.'));
            return;
          }
          resolve(result);
        },
        'image/webp',
        0.9,
      );
    });

    if (blob.type !== 'image/webp') {
      throw new Error('The browser could not convert that image to WebP.');
    }
    return blob;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to decode the tile image.'));
    image.src = src;
  });
}
